/**
 * src/utils/timeAgo.js
 *
 * Shared relative-time formatter using dayjs.
 * Returns human-readable strings like "Just now", "3m ago", "2h ago", "5d ago".
 */
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';

// Extend dayjs with plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const UTC_NAIVE_RE = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}(?:\.\d+)?$/;
const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const FUTURE_SKEW_MS = 60 * 1000;
const JUST_NOW_MS = 10 * 1000;

function normalizeTimestampInput(input) {
    if (!input) return null;

    if (typeof input === 'number') {
        // Heuristic: treat values < 1e12 as epoch seconds and normalize to ms.
        return input < 1e12 ? input * 1000 : input;
    }

    if (input instanceof Date) return new Date(input.getTime());

    const value = String(input).trim();
    if (!value) return null;

    if (/[zZ]$|[+\-]\d{2}:\d{2}$/.test(value)) {
        return value;
    }

    if (UTC_NAIVE_RE.test(value)) {
        return `${value.replace(' ', 'T')}Z`;
    }

    if (DATE_ONLY_RE.test(value)) {
        return `${value}T00:00:00.000Z`;
    }

    return value;
}

export function parseTimestamp(input) {
    const normalized = normalizeTimestampInput(input);
    if (!normalized) return null;

    if (typeof normalized === 'number' || normalized instanceof Date) {
        const direct = new Date(normalized);
        return Number.isNaN(direct.getTime()) ? null : direct;
    }

    const raw = String(normalized);

    // For timezone-naive SQL datetime strings, accept mixed historical data by
    // choosing the interpretation (UTC vs local) closest to now.
    if (UTC_NAIVE_RE.test(raw)) {
        const localIso = raw.replace(' ', 'T');
        const utcIso = `${localIso}Z`;

        const asUtc = new Date(utcIso);
        const asLocal = new Date(localIso);

        const utcValid = !Number.isNaN(asUtc.getTime());
        const localValid = !Number.isNaN(asLocal.getTime());

        if (utcValid && localValid) {
            const now = Date.now();
            const utcDistance = Math.abs(asUtc.getTime() - now);
            const localDistance = Math.abs(asLocal.getTime() - now);
            return localDistance < utcDistance ? asLocal : asUtc;
        }

        if (utcValid) return asUtc;
        if (localValid) return asLocal;
        return null;
    }

    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
}

export function isFutureTimestamp(input, futureSkewMs = FUTURE_SKEW_MS) {
    const date = parseTimestamp(input);
    if (!date) return false;
    return date.getTime() - Date.now() > futureSkewMs;
}

/**
 * Convert timestamp to relative time string (e.g., "5 minutes ago")
 * Handles ISO-8601 strings and Date objects consistently.
 */
export function timeAgo(dateStr) {
    if (!dateStr) return "—";
    
    try {
        const date = parseTimestamp(dateStr);
        
        if (!date) {
            console.warn('[timeAgo] Invalid date:', dateStr);
            return "—";
        }

        const diffMs = date.getTime() - Date.now();

        if (diffMs > 0 && diffMs <= FUTURE_SKEW_MS) {
            return 'Just now';
        }

        if (diffMs <= 0 && Math.abs(diffMs) < JUST_NOW_MS) {
            return 'Just now';
        }

        const absMs = Math.abs(diffMs);
        const sec = Math.floor(absMs / 1000);
        const min = Math.floor(absMs / 60000);
        const hr = Math.floor(absMs / 3600000);
        const day = Math.floor(absMs / 86400000);
        const month = Math.floor(day / 30);
        const year = Math.floor(day / 365);

        const suffix = diffMs < 0 ? 'ago' : 'from now';
        if (sec < 60) return diffMs < 0 ? `${sec}s ago` : `in ${sec}s`;
        if (min < 60) return diffMs < 0 ? `${min}m ago` : `in ${min}m`;
        if (hr < 24) return diffMs < 0 ? `${hr}h ago` : `in ${hr}h`;
        if (day < 30) return diffMs < 0 ? `${day}d ago` : `in ${day}d`;
        if (month < 12) return diffMs < 0 ? `${month}mo ago` : `in ${month}mo`;
        return diffMs < 0 ? `${year}y ago` : `in ${year}y`;
    } catch (err) {
        console.warn('[timeAgo] Error parsing date:', dateStr, err);
        return "—";
    }
}

/**
 * Returns the full formatted timestamp for tooltip display with timezone.
 * Format: "Mar 09, 2026 2:30:45 PM"
 */
export function exactTime(dateStr) {
    if (!dateStr) return "";
    
    try {
        const date = parseTimestamp(dateStr);
        
        if (!date) {
            return "";
        }
        
        return dayjs(date).format("MMM DD, YYYY h:mm:ss A");
    } catch (err) {
        console.warn('[exactTime] Error parsing date:', dateStr, err);
        return "";
    }
}
